import type { TrafficCalculatorSliderRange } from "@/components/services/seo/seo-revenue-projection";

export type SemProfitField =
  | "budget"
  | "cpc"
  | "conversion"
  | "close"
  | "customerValue";

export const SEM_PROFIT_SLIDER_RANGES = {
  budget: {
    min: 5_000,
    max: 300_000,
    step: 5_000,
    dragStep: 1_000,
    fallback: 30_000,
  },
  cpc: {
    min: 5,
    max: 250,
    step: 5,
    dragStep: 1,
    fallback: 35,
  },
  conversion: {
    min: 1,
    max: 20,
    step: 0.5,
    dragStep: 0.5,
    fallback: 5,
  },
  close: {
    min: 5,
    max: 80,
    step: 1,
    dragStep: 1,
    fallback: 25,
  },
  customerValue: {
    min: 1_000,
    max: 150_000,
    step: 1_000,
    dragStep: 500,
    fallback: 15_000,
  },
} as const satisfies Record<SemProfitField, TrafficCalculatorSliderRange>;

export type SemProfitInputs = {
  budget: number;
  cpc: number;
  conversionPercent: number;
  closePercent: number;
  customerValue: number;
};

export type SemProfitProjection = {
  clicks: number;
  leads: number;
  customers: number;
  projectedValue: number;
  valuePerAdKrona: number;
  costPerCustomer: number;
  breakEvenCpc: number;
};

const toRate = (percent: number) =>
  Math.min(100, Math.max(0, percent)) / 100;

export function projectSemProfit(inputs: SemProfitInputs): SemProfitProjection {
  const budget = Math.max(0, inputs.budget);
  const cpc = Math.max(0.01, inputs.cpc);
  const conversion = toRate(inputs.conversionPercent);
  const close = toRate(inputs.closePercent);
  const customerValue = Math.max(0, inputs.customerValue);

  const clicks = budget / cpc;
  const leads = clicks * conversion;
  const customers = leads * close;
  const projectedValue = customers * customerValue;
  const valuePerAdKrona = budget > 0 ? projectedValue / budget : 0;
  const costPerCustomer = customers > 0 ? budget / customers : 0;
  const breakEvenCpc = customerValue * conversion * close;

  return {
    clicks,
    leads,
    customers,
    projectedValue,
    valuePerAdKrona,
    costPerCustomer,
    breakEvenCpc,
  };
}
